import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as ZonesSubscriptionsActions from '../actions/zones-subscriptions.actions';
import * as ZonesSubscriptionsApiActions from '../actions/zones-subscriptions-api.actions';
import { ZoneSubscriptionsService } from '../../../zones/services/zone-subscriptions.service';
import { ZoneSubscription } from '../../../zones/models/zone-subscription';
import { ShapeDataFeature } from '../../../zones/models/shape-data-feature';
import { ShapeDataFeatureCollection } from '../../../zones/models/shape-data-feature-collection';
import { ShapeHelper } from '../../../shared/helpers/shape.helper';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { LoadingBarService } from '@ngx-loading-bar/core';

@Injectable()
export class ZonesSubscriptionsEffects {

  getZonesSubscriptions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.getZonesSubscriptions.type),
      switchMap((action: any) => this._zoneSubscriptionsService.getZones()
        .pipe(
          exhaustMap((data) => {
            if (data) {
              data.forEach((zone: ZoneSubscription) => {
                this._normalizeZone(zone);
              });
            }
            return of(ZonesSubscriptionsApiActions.getZonesSubscriptionsSuccess({data: data}))
          }),
          catchError(error => of(ZonesSubscriptionsApiActions.getZonesSubscriptionsFailure({error})))
        )
      )
    )
  );

  createZonesSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.createZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.createZone(action.data)
        .pipe(
          exhaustMap((zone) => {
            this._normalizeZone(zone);
            return of(ZonesSubscriptionsApiActions.createZoneSubscriptionSuccess({data: zone}))
          }),
          catchError(error => of(ZonesSubscriptionsApiActions.createZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  editZonesSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.editZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.editZone(action.data)
        .pipe(
          exhaustMap((zone) => {
            this._normalizeZone(zone);
            return of(ZonesSubscriptionsApiActions.editZoneSubscriptionSuccess({data: zone}));
          }),
          catchError(error => of(ZonesSubscriptionsApiActions.editZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  editUnitsZoneSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.editUnitsZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.editUnitsZone(action.data)
        .pipe(
          exhaustMap((zone) => {
            return of(ZonesSubscriptionsApiActions.editUnitsZoneSubscriptionSuccess({data: zone}));
          }),
            catchError(error => of(ZonesSubscriptionsApiActions.editUnitsZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  deleteZonesSubscription$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.deleteZoneSubscription.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.deleteZone(action.zoneId)
        .pipe(
          exhaustMap((res) => of(ZonesSubscriptionsApiActions.deleteZoneSubscriptionSuccess({zoneId: res}))),
          catchError(error => of(ZonesSubscriptionsApiActions.deleteZoneSubscriptionFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  changeState$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ZonesSubscriptionsActions.changeState.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._zoneSubscriptionsService.changeState(action.data)
        .pipe(
          exhaustMap((res) => of(ZonesSubscriptionsApiActions.changeStateSuccess({data: res}))),
          catchError(error => of(ZonesSubscriptionsApiActions.changeStateFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  private _loader: LoadingBarState;

  constructor(
    private _actions$: Actions,
    private _zoneSubscriptionsService: ZoneSubscriptionsService,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }

  private _normalizeZone(zone: ZoneSubscription) {
    let shapeData: ShapeDataFeatureCollection = {
      type: 'FeatureCollection',
      features: []
    };
    if (zone.shapeData['type'] === 'Feature') {
      const feature = ShapeHelper.createPolygonFromCircle(<ShapeDataFeature>zone.shapeData, zone.zoneSubscriptionId, (<ShapeDataFeature>zone.shapeData)?.properties?.shapeId);
      if (feature) {
        shapeData.features.push(feature);
        zone.shapeData = shapeData;
      }
    } else if (zone.shapeData['type'] === 'FeatureCollection') {
      (<ShapeDataFeatureCollection>zone.shapeData).features.forEach((x: ShapeDataFeature) => {
        const feature = ShapeHelper.createPolygonFromCircle(x, zone.zoneSubscriptionId, x.properties.shapeId);
        if (feature) {
          shapeData.features.push(feature);
        }
      });
      zone.shapeData = shapeData;
    }
  }
}
